import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../contexts/LanguageContext';
import { publicApi } from '../utils/publicApi';
import { PROFILE_COMPANY_CAPITAL_OPTIONS, PROFILE_SENIORITY_OPTIONS } from '../data/profileFieldOptions';
import heroImage from '../assets/img/HeroImageAdvOm.png';

const DEFAULT_START_DATE = '2026-04-27';

const parseOverviewDate = (value) => {
  if (!value) return new Date(`${DEFAULT_START_DATE}T00:00:00`);

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  return new Date(`${DEFAULT_START_DATE}T00:00:00`);
};

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan',
  'Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia',
  'Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo (Congo-Brazzaville)','Costa Rica',
  "Côte d'Ivoire",'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador',
  'Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia','Fiji','Finland','France',
  'Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau',
  'Guyana','Haiti','Honduras','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar',
  'Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia',
  'Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal',
  'Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway','Oman','Pakistan',
  'Palau','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania',
  'Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal',
  'Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea',
  'South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria','Taiwan','Tajikistan',
  'Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela',
  'Vietnam','Yemen','Zambia','Zimbabwe'
];

const AttendInterestModal = ({ isOpen, onClose, isRTL }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    designation: '',
    country: '',
    sector: '',
    company_capital_size: '',
    seniority_level: '',
    tourism_muscat: false,
    tourism_oman_overnight: false,
    email: '',
    phone: '',
    linkedin: '',
    website: '',
    whyAttend: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let next = type === 'checkbox' ? checked : value;
    if (name === 'phone') {
      next = String(value).replace(/\D/g, '');
    }
    setFormData((prev) => ({ ...prev, [name]: next }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setErrorMessage('');
    setSuccessMessage('');
  };

  const validate = () => {
    const newErrors = {};
    const name = String(formData.name || '').trim();
    const company = String(formData.company || '').trim();
    const designation = String(formData.designation || '').trim();
    const sector = String(formData.sector || '').trim();
    const email = String(formData.email || '').trim();
    const phone = String(formData.phone || '').trim();
    const whyAttend = String(formData.whyAttend || '').trim();
    const companyCapital = String(formData.company_capital_size || '').trim();
    const seniority = String(formData.seniority_level || '').trim();

    if (!name) {
      newErrors.name = t('website.attend.required');
    } else if (name.length < 3 || name.length > 40) {
      newErrors.name = t('website.attend.validationNameLength');
    }

    if (!company) {
      newErrors.company = t('website.attend.required');
    } else if (company.length < 1 || company.length > 35) {
      newErrors.company = t('website.attend.validationCompanyLength');
    }

    if (!designation) {
      newErrors.designation = t('website.attend.required');
    } else if (designation.length < 1 || designation.length > 35) {
      newErrors.designation = t('website.attend.validationDesignationLength');
    }

    if (!sector) {
      newErrors.sector = t('website.attend.required');
    } else if (sector.length < 1 || sector.length > 35) {
      newErrors.sector = t('website.attend.validationSectorLength');
    }

    if (!companyCapital) {
      newErrors.company_capital_size = t('website.attend.validationCapitalRequired');
    }
    if (!seniority) {
      newErrors.seniority_level = t('website.attend.validationSeniorityRequired');
    }

    if (!email) {
      newErrors.email = t('website.attend.required');
    } else if (!emailRegex.test(email)) {
      newErrors.email = t('website.attend.validationEmail');
    }

    if (phone && !/^\d+$/.test(phone)) {
      newErrors.phone = t('website.attend.validationPhoneDigits');
    }

    if (!whyAttend) {
      newErrors.whyAttend = t('website.attend.required');
    } else if (whyAttend.length < 3 || whyAttend.length > 1000) {
      newErrors.whyAttend = t('website.attend.validationWhyAttendLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        name: String(formData.name || '').trim(),
        company: String(formData.company || '').trim(),
        designation: String(formData.designation || '').trim(),
        country: String(formData.country || '').trim() || undefined,
        sector: String(formData.sector || '').trim(),
        company_capital_size: String(formData.company_capital_size || '').trim(),
        seniority_level: String(formData.seniority_level || '').trim(),
        tourism_muscat: Boolean(formData.tourism_muscat),
        tourism_oman_overnight: Boolean(formData.tourism_oman_overnight),
        email: String(formData.email || '').trim().toLowerCase(),
        phone: String(formData.phone || '').trim() || undefined,
        linkedin: String(formData.linkedin || '').trim() || undefined,
        website: String(formData.website || '').trim() || undefined,
        whyAttend: String(formData.whyAttend || '').trim(),
      };
      await publicApi.createInterestedWith(payload);

      try {
        const serviceId = 'service_t2nzyna';
        const templateId = 'template_4ie4lue';
        const publicKey = 'vAdPXkWlaHzaiu3gW';

        const capOpt = PROFILE_COMPANY_CAPITAL_OPTIONS.find((o) => o.value === payload.company_capital_size);
        const senOpt = PROFILE_SENIORITY_OPTIONS.find((o) => o.value === payload.seniority_level);
        const capitalLabel = capOpt ? t(capOpt.labelKey) : payload.company_capital_size;
        const seniorityLabel = senOpt ? t(senOpt.labelKey) : payload.seniority_level;

        const messageLines = [
          'New "Express Your Interest to Attend" submission:',
          '',
          `Name: ${payload.name}`,
          `Company: ${payload.company}`,
          `Designation: ${payload.designation}`,
          `Country: ${payload.country || '-'}`,
          `Sector of Interest: ${payload.sector}`,
          `Company Capital Size: ${capitalLabel}`,
          `Seniority Level: ${seniorityLabel}`,
          `Muscat Tour: ${payload.tourism_muscat ? 'Yes' : 'No'}`,
          `Oman Tour (overnight): ${payload.tourism_oman_overnight ? 'Yes' : 'No'}`,
          `Email: ${payload.email}`,
          `Contact: ${payload.phone || '-'}`,
          `LinkedIn profile: ${payload.linkedin || '-'}`,
          `Company website: ${payload.website || '-'}`,
          '',
          'Why should you attend?',
          payload.whyAttend,
        ];

        const templateParams = {
          from_name: payload.name,
          email: payload.email,
          reply_to: payload.email,
          to_email: 'advantageoman@investoman.om',
          message: messageLines.join('\n'),
          to_name: 'Invest Oman Team',
        };

        await emailjs.send(serviceId, templateId, templateParams, publicKey);
      } catch (emailErr) {
        console.error('Database save succeeded, but email notification failed:', emailErr);
      }

      setSuccessMessage(t('website.attend.successMessage'));
      setFormData({
        name: '',
        company: '',
        designation: '',
        country: '',
        sector: '',
        company_capital_size: '',
        seniority_level: '',
        tourism_muscat: false,
        tourism_oman_overnight: false,
        email: '',
        phone: '',
        linkedin: '',
        website: '',
        whyAttend: '',
      });

      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (err) {
      console.error('Error submitting attend interest:', err);
      setErrorMessage(err.message || t('website.attend.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[2100] flex items-center justify-center px-4 sm:px-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.97 }}
          transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
          className="relative w-full max-w-3xl rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden"
          dir={isRTL ? 'rtl' : 'ltr'}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between gap-3">
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">
                {t('website.attend.title')}
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                {t('website.attend.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="px-6 sm:px-8 py-5 max-h-[75vh] overflow-y-auto">
            {errorMessage && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {errorMessage}
              </div>
            )}
            {successMessage && (
              <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.name')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    maxLength={40}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all ${
                      errors.name ? 'border-red-400' : 'border-slate-200'
                    }`}
                  />
                  {errors.name && <p className="mt-1 text-[11px] text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.company')} *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    maxLength={35}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all ${
                      errors.company ? 'border-red-400' : 'border-slate-200'
                    }`}
                  />
                  {errors.company && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.company}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.designation')} *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    maxLength={35}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all ${
                      errors.designation ? 'border-red-400' : 'border-slate-200'
                    }`}
                  />
                  {errors.designation && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.designation}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.country')}
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all bg-white ${
                      errors.country ? 'border-red-400' : 'border-slate-200'
                    }`}
                  >
                    <option value="">{isRTL ? 'اختر الدولة' : 'Select country'}</option>
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.country}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.sector')} *
                  </label>
                  <input
                    type="text"
                    name="sector"
                    value={formData.sector}
                    onChange={handleChange}
                    maxLength={35}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all ${
                      errors.sector ? 'border-red-400' : 'border-slate-200'
                    }`}
                  />
                  {errors.sector && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.sector}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.companyCapitalSize')} *
                  </label>
                  <select
                    name="company_capital_size"
                    value={formData.company_capital_size}
                    onChange={handleChange}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all bg-white ${
                      errors.company_capital_size ? 'border-red-400' : 'border-slate-200'
                    }`}
                  >
                    <option value="">{t('website.attend.fields.selectCapital')}</option>
                    {PROFILE_COMPANY_CAPITAL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {t(o.labelKey)}
                      </option>
                    ))}
                  </select>
                  {errors.company_capital_size && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.company_capital_size}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.seniorityLevel')} *
                  </label>
                  <select
                    name="seniority_level"
                    value={formData.seniority_level}
                    onChange={handleChange}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all bg-white ${
                      errors.seniority_level ? 'border-red-400' : 'border-slate-200'
                    }`}
                  >
                    <option value="">{t('website.attend.fields.selectSeniority')}</option>
                    {PROFILE_SENIORITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {t(o.labelKey)}
                      </option>
                    ))}
                  </select>
                  {errors.seniority_level && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.seniority_level}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.email')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all ${
                      errors.email ? 'border-red-400' : 'border-slate-200'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-[11px] text-red-500">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    inputMode="numeric"
                    autoComplete="tel"
                    className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all ${
                      errors.phone ? 'border-red-400' : 'border-slate-200'
                    }`}
                  />
                  {errors.phone && <p className="mt-1 text-[11px] text-red-500">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.linkedin')}
                  </label>
                  <input
                    type="text"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    {t('website.attend.fields.website')}
                  </label>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all"
                  />
                </div>
              </div>

              <div className="space-y-3 pt-1">
                <p className="text-xs font-medium text-slate-700">{t('website.attend.fields.tourismProgramsHeading')}</p>
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:border-[#492E8B]/30 transition-colors">
                  <input
                    type="checkbox"
                    name="tourism_muscat"
                    checked={Boolean(formData.tourism_muscat)}
                    onChange={handleChange}
                    className="mt-0.5 rounded border-slate-300 text-[#492E8B] focus:ring-[#492E8B]/20"
                  />
                  <span className="text-sm text-slate-700">{t('website.attend.fields.tourismMuscat')}</span>
                </label>
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:border-[#492E8B]/30 transition-colors">
                  <input
                    type="checkbox"
                    name="tourism_oman_overnight"
                    checked={Boolean(formData.tourism_oman_overnight)}
                    onChange={handleChange}
                    className="mt-0.5 rounded border-slate-300 text-[#492E8B] focus:ring-[#492E8B]/20"
                  />
                  <span className="text-sm text-slate-700">{t('website.attend.fields.tourismOmanOvernight')}</span>
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  {t('website.attend.fields.whyAttend')} *
                </label>
                <textarea
                  name="whyAttend"
                  value={formData.whyAttend}
                  onChange={handleChange}
                  rows={3}
                  maxLength={1000}
                  className={`w-full rounded-2xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all ${
                    errors.whyAttend ? 'border-red-400' : 'border-slate-200'
                  }`}
                  placeholder="Brief about your company or why should you attend"
                />
                {errors.whyAttend && (
                  <p className="mt-1 text-[11px] text-red-500">{errors.whyAttend}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  {t('dashboard.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-[50px] text-xs sm:text-sm font-semibold text-white transition-opacity disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(157deg, #0AB0CE 0%, #111034 100%)',
                    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {submitting ? t('website.attend.submitting') : t('website.attend.submit')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const Home = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [timeLeft, setTimeLeft] = useState({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00'
  });
  const [countdownFinished, setCountdownFinished] = useState(false);
  const [eventPassed, setEventPassed] = useState(false);
  const { t, isRTL } = useLanguage();
  const [isAttendOpen, setIsAttendOpen] = useState(false);
  const eventStartDate = overviewData?.start_date || DEFAULT_START_DATE;
  const eventStartDateObject = useMemo(
    () => parseOverviewDate(eventStartDate),
    [eventStartDate]
  );

  useEffect(() => {
    let isMounted = true;

    const loadOverview = async () => {
      try {
        const data = await publicApi.getOverview();
        if (isMounted && data) {
          setOverviewData(data);
        }
      } catch (error) {
        console.error('Error loading overview for home:', error);
      }
    };

    loadOverview();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const targetDate = eventStartDateObject.getTime();
      const now = new Date().getTime();
      const difference = targetDate - now;

      const fourDaysAfterEvent = targetDate + (4 * 24 * 60 * 60 * 1000);
      if (now >= fourDaysAfterEvent) {
        setEventPassed(true);
      } else {
        setEventPassed(false);
      }

      if (difference <= 0) {
        setCountdownFinished(true);
        return {
          days: '00',
          hours: '00',
          minutes: '00',
          seconds: '00'
        };
      }
      setCountdownFinished(false);

      return {
        days: String(Math.floor(difference / (1000 * 60 * 60 * 24))).padStart(2, '0'),
        hours: String(Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))).padStart(2, '0'),
        minutes: String(Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0'),
        seconds: String(Math.floor((difference % (1000 * 60)) / 1000)).padStart(2, '0')
      };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [eventStartDateObject]);

  const eventDateLabel = useMemo(() => {
    return eventStartDateObject.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, [eventStartDateObject]);

  const countdownItems = [
    { value: timeLeft.days, label: t('website.home.countdownLabels.days') },
    { value: timeLeft.hours, label: t('website.home.countdownLabels.hours') },
    { value: timeLeft.minutes, label: t('website.home.countdownLabels.minutes') },
    { value: timeLeft.seconds, label: t('website.home.countdownLabels.seconds') },
  ];

  return (
    <section id="home" className="relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero fills full viewport height so next section doesn't peek through */}
      <div className="relative w-full min-h-screen bg-[#F1F3F7] overflow-hidden">

        {/* Background image covering full hero */}
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          decoding="async"
        />

        {/* Centered content block */}
        <div className="absolute inset-0 flex items-center justify-center pt-[64px] sm:pt-[80px] lg:pt-[88px] [@media(max-height:700px)]:pt-8">
          <div className="flex flex-col items-center justify-center text-center gap-6 sm:gap-8 lg:gap-10 [@media(max-height:700px)]:gap-4 px-6 sm:px-10 w-full max-w-[1033px]">

            {/* Static title (no overview DB title/subtitle) */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 0.61, 0.36, 1] }}
              className="w-full flex flex-col items-center text-center text-balance text-[30px] sm:text-[40px] lg:text-[48px] [@media(max-height:700px)]:text-[38px] lg:mt-8"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: '64px',
              }}
            >
              <span className="block w-full text-center" style={{ fontWeight: 300, color: 'white' }}>
                {t('website.home.heroTitleFirst')}
                <br />
              </span>
              <span
                className="block w-full text-center"
                style={{
                  fontWeight: 600,
                  background: 'linear-gradient(to bottom, #FFFFFF 0%, rgb(124, 153, 180) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('website.home.heroTitleSecond')}
              </span>
            </motion.h1>

            {/* Static subtitle (no overview DB subtitle) */}
            <motion.p
  initial={{ opacity: 0, y: 16 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
  className="w-full max-w-[700px] mx-auto text-center text-balance text-white text-[14px] sm:text-[16px]"
  style={{
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontWeight: 400,
    lineHeight: '24px',
  }}
>
              {t('website.home.heroSubtitleLine1')} <br />
              {t('website.home.heroSubtitleLine2')}
</motion.p>

            {/* Date — Plus Jakarta Sans, 32px, weight 600, line-height 84 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
              className="w-full flex justify-center text-center text-[20px] sm:text-[26px] lg:text-[32px] [@media(max-height:700px)]:text-[24px]"
            >
              <span
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  lineHeight: '84px',
                  background: 'linear-gradient(to bottom, #FFFFFF 30%, rgb(124, 153, 180) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'inline-block',
                  textAlign: 'center',
                }}
              >
                {eventDateLabel}
              </span>
            </motion.div>

            {/* Countdown — Figma: 83×83, padding 12.77/19.15, rgba(255,255,255,0.06), inner column gap 12.77 */}
            {!countdownFinished && !eventPassed && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
                className="inline-flex items-center justify-center gap-3 sm:gap-[17.24px] [@media(max-height:700px)]:gap-2"
              >
                {countdownItems.map((item) => (
                  <div
                    key={item.label}
                    className="box-border inline-flex items-center justify-center shrink-0 scale-[0.72] sm:scale-90 lg:scale-100 [@media(max-height:700px)]:scale-[0.78]"
                    style={{
                      width: 83,
                      height: 83,
                      paddingLeft: 12.77,
                      paddingRight: 12.77,
                      paddingTop: 19.15,
                      paddingBottom: 19.15,
                      background: 'rgba(255, 255, 255, 0.06)',
                      boxShadow: '0px 0.8122919797897339px 9.747504234313965px rgba(0, 0, 0, 0.05)',
                      borderRadius: 9.75,
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <div className="inline-flex flex-col items-center justify-start">
                      <div
                        className="text-white"
                        style={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: 30.65,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {item.value}
                      </div>
                      <div
                        className="text-white text-center"
                        style={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: 16.6,
                          fontWeight: 700,
                          lineHeight: 1.15,
                        }}
                      >
                        {item.label}
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Event started banner */}
            {countdownFinished && !eventPassed && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.45 }}
                className="max-w-[640px] mx-auto w-full rounded-[24px] bg-white/10 backdrop-blur-md border border-white/20 px-6 py-5 text-center text-white"
              >
                <div className="text-2xl md:text-3xl font-bold text-center text-balance">
                  {t('website.home.eventStartedTitle')}
                </div>
                <div className="mt-2 text-white/90 text-base md:text-lg font-medium text-center text-balance">
                  {t('website.home.eventStartedDescription')}
                </div>
              </motion.div>
            )}

            {/* CTA — Figma: h54, px18, gradient bg, rounded-50, Plus Jakarta Sans 600, 16px */}
            <motion.button
              type="button"
              onClick={() => setIsAttendOpen(true)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="h-[48px] sm:h-[54px] px-5 sm:px-[18px] inline-flex items-center gap-[6px] rounded-full text-white border border-white"
              style={{
                background: 'linear-gradient(90deg, rgba(171, 159, 202, 0.50) 0%, rgba(133, 115, 177, 0.50) 100%)',
                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              <span className="text-[14px] sm:text-[16px] font-semibold leading-[24px]">
                {t('website.attend.ctaLabel')}
              </span>
              <svg
                width="7"
                height="12"
                viewBox="0 0 10 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M1.5 1.5L7.5 7L1.5 12.5"
                  stroke="white"
                  strokeWidth="1.29"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.button>

          </div>
        </div>
      </div>

      <AttendInterestModal
        isOpen={isAttendOpen}
        onClose={() => setIsAttendOpen(false)}
        isRTL={isRTL}
      />
    </section>
  );
};

export default Home;
